import React from 'react';
import { Link } from 'react-router-dom';
import { Category } from '../../types';
import { categoryColor, categorySlug } from '../../utils/categoryColors';
import styles from './CategoryCard.module.css';

interface CategoryCardProps {
  category: Category;
  count: number;
  index: number;
  newCount?: number;
  featured?: boolean;
}

const symbol: Record<Category, string> = {
  Transparency: '◉',
  Control: '⌘',
  'Error Handling': '↺',
  Onboarding: '✧',
  Conversation: '≋',
};

const blurb: Record<Category, string> = {
  Transparency: 'Make the agent’s actions, reasoning, and uncertainty easier to understand.',
  Control: 'Give people meaningful oversight of consequential agent actions.',
  'Error Handling': 'Help people recover when the agent gets something wrong.',
  Onboarding: 'Introduce what the agent can do, without overwhelming the people using it.',
  Conversation: 'Build dialogues that hold context, handle changes, and make sense over time.',
};

export function CategoryCard({ category, count, index, newCount = 0, featured = false }: CategoryCardProps) {
  const tone = { '--tone': categoryColor[category] } as React.CSSProperties;

  const body = (
    <div className={styles.body}>
      <div className={styles.top}>
        <span className={styles.symbol} aria-hidden="true">{symbol[category]}</span>
        <span className={styles.number}>
          {String(index + 1).padStart(2, '0')} / {count} {count === 1 ? 'pattern' : 'patterns'}
          {newCount > 0 && <span className={styles.newCount}> · {newCount} new</span>}
        </span>
      </div>
      <h3 className={styles.name}>{category}</h3>
      <p className={styles.desc}>{blurb[category]}</p>
      <span className={styles.label}>Explore {category.toLowerCase()}</span>
    </div>
  );

  return (
    <Link
      to={`/category/${categorySlug[category]}`}
      className={`${styles.card} ${featured ? `${styles.featured} tint-glow` : ''}`}
      style={{ ...tone, '--i': index } as React.CSSProperties}
      data-reveal
    >
      {body}
      {featured && (
        <div className={styles.preview} aria-hidden="true">
          “Let’s pick up where we left off.”
          <span>
            Context remembered.
            <br />
            Next step, understood.
          </span>
        </div>
      )}
    </Link>
  );
}
